import { Button, Card, Collapse, Form, Input, Spin, Table } from 'antd';
import { useEffect, useState } from 'react';
import {
    useCreateUserFromScriptSureUserMutation,
    useGetScriptSureUserMutation,
} from '../../features/organization/organizationService';
import OrganizationCollapseView from '../../components/Organizations/OrganizationCollapseView';
import { RightOutlined, UnderlineOutlined } from '@ant-design/icons';
import { HospiceOrganizationWithDetailsResponse } from '../../models/OrganizationModels';
import Title from 'antd/lib/skeleton/Title';
import RoundedButton from '../../components/RoundedButton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ScriptSureUserWithDetailsDto,
    UserWithDetailsDto,
} from '../../models/UserResponses';
import PageTitle from '../../components/PageTitle';
import OrganizationView from '../../components/Organizations/OrganizationView';
import { Utility } from '../../utilities/Utility';
import OrganizationUserImportDetails from './OrganizationUserImportDetails';
import ConfirmationModal from '../../components/Orders/ConfirmationModal';
const { Panel } = Collapse;

type FieldType = {
    scriptSureUserId?: string;
};

const OrganizationUserImport = () => {
    const { id: organizationId } = useParams();
    const [getUser, { isLoading: isGetSsUserLoading }] =
        useGetScriptSureUserMutation();
    const [createUser, { isLoading: isCreateUserLoading }] =
        useCreateUserFromScriptSureUserMutation();
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);

    const [form] = Form.useForm();

    const navigate = useNavigate();

    const [userId, setUserId] = useState<string>('');
    const [user, setUser] = useState<ScriptSureUserWithDetailsDto>();

    const setFailedFieldMessage = () => {
        form.setFields([
            {
                name: 'searchUser',
                errors: [
                    'User ID not found or import failed. Please verify the ID and try again.',
                ],
            },
        ]);
    };

    const setEmptyFieldMessage = () => {
        form.setFields([
            {
                name: 'searchUser',
                errors: ['Please Enter ScriptSure User ID.'],
            },
        ]);
    };

    const resetUser = () => {
        setUser(undefined);
        setUserId('');
        form.resetFields();
        setIsSuccessModalOpen(false);
        setIsFailureModalOpen(false);
    };

    const fetchUser = async () => {
        try {
            if (!Utility.isNullOrUndefinedOrEmpty(userId)) {
                const response: any = await getUser(userId);

                if (response.error) {
                    setFailedFieldMessage();
                } else {
                    setUser(response.data);
                }
            } else {
                setEmptyFieldMessage();
            }
        } catch (error) {
            setFailedFieldMessage();
        }
    };

    const postUser = async () => {
        try {
            if (!user) return;

            const response: any = await createUser(user);

            if (response.error) {
                setIsFailureModalOpen(true);
            } else {
                setIsSuccessModalOpen(true);
            }
        } catch (error) {
            setIsFailureModalOpen(true);
        }
    };

    return (
        <Spin spinning={isGetSsUserLoading || isCreateUserLoading}>
            <PageTitle title="Import New User" onClick={() => navigate(-1)} />
            <div className="d-flex flex-row align-items-center p-3 ms-5 shadow min-width-700">
                <Form
                    form={form}
                    name="basic"
                    layout="vertical"
                    className="ms-4"
                    autoComplete="off"
                >
                    <Form.Item<FieldType>
                        label={
                            <label className="field-title">
                                ScriptSure User ID
                            </label>
                        }
                        name="searchUser"
                        rules={[
                            {
                                required: true,
                                validator: (_, value) => {
                                    if (!value) {
                                        setEmptyFieldMessage();
                                        return Promise.reject();
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input
                            allowClear={false}
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            style={{ height: '41px', width: '27rem' }}
                            placeholder="Enter ScriptSure User ID"
                        />
                    </Form.Item>
                </Form>

                <RoundedButton
                    onClick={fetchUser}
                    className="bg-light-blue text-white pt-2 pb-2 ps-4 pe-4 h-25 w-10 fs-6 ms-5"
                >
                    Import User
                </RoundedButton>
            </div>

            {user && (
                <div className="mt-5 d-flex flex-column p-5 ms-5 shadow min-width-700">
                    <OrganizationUserImportDetails data={user} />

                    <div className="d-flex flex-row">
                        <RoundedButton
                            className="bg-white border-light-blue border text-light-blue pt-2 pb-2 ps-4 pe-4 h-25 fs-6"
                            style={{ width: '180px' }}
                            onClick={resetUser}
                        >
                            Cancel
                        </RoundedButton>
                        <RoundedButton
                            className="bg-light-blue text-white pt-2 pb-2 ps-4 pe-4 h-25 fs-6 ms-3"
                            style={{ width: '180px' }}
                            onClick={postUser}
                        >
                            Confirm Import
                        </RoundedButton>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={isSuccessModalOpen || isFailureModalOpen}
                onClose={() =>
                    isSuccessModalOpen
                        ? setIsSuccessModalOpen(false)
                        : setIsFailureModalOpen(false)
                }
                isSuccess={isSuccessModalOpen}
                title={isSuccessModalOpen ? 'Successful!' : 'Unsuccessful!'}
                message={
                    isSuccessModalOpen
                        ? 'The user is successfully imported to NaviScript.'
                        : 'Failed to import the user to NaviScript.\n Please try again.'
                }
                primaryButtonText={
                    isSuccessModalOpen ? 'Import New User' : 'Try Again'
                }
                secondaryButtonText="View User List"
                onPrimaryButtonLinkToClick={resetUser}
                onSecondaryButtonLinkClick={() => navigate(-1)}
            />
        </Spin>
    );
};

export default OrganizationUserImport;
